// components/AppGuard.js - FIXED VERSION
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useEffect, useState, useRef } from 'react';
import { getRouteConfig, isPublicRoute, getRoleBasedRedirect } from '../routes/routeConfig';
import { Progress } from '../components/ui/progress';
import * as types from '../store/auth/login/store/_actionTypes';

const AppGuard = ({ children }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { isAuthenticated, user, loading, initialCheckDone } = useSelector(state => state.auth);
  const [routeChecked, setRouteChecked] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const hasInitialized = useRef(false);

  // A "passive" public route renders the same for anonymous and authenticated
  // visitors (redirectIfAuthenticated is false/unset). Marketing/legal pages.
  // We skip auth checks and loading screens for these so visitors see content
  // immediately — auth only runs once the user actually heads to /auth/login.
  const currentPath = router.pathname;
  const currentRouteConfig = getRouteConfig(currentPath);
  const isPassivePublic =
    isPublicRoute(currentPath) && !currentRouteConfig?.redirectIfAuthenticated;

  // Client-side hydration check
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Auth initialization logic
  useEffect(() => {
    if (!isClient) return;

    const isPublic = isPublicRoute(currentPath);

    // Passive public routes don't need an auth check to render.
    if (isPassivePublic) {
      return;
    }

    // Public auth pages (login/register) need a check so that already-logged-in
    // visitors get redirected to their dashboard.
    if (isPublic && !hasInitialized.current && !initialCheckDone) {
      console.log('📤 Public auth route: dispatching CHECK_AUTH');
      hasInitialized.current = true;
      dispatch({ type: types.CHECK_AUTH });
      return;
    }

    // For protected routes, always check auth
    if (!isPublic && !hasInitialized.current && !initialCheckDone && !loading) {
      console.log('📤 Protected route: dispatching CHECK_AUTH');
      hasInitialized.current = true;
      dispatch({ type: types.CHECK_AUTH });
    }

  }, [dispatch, initialCheckDone, loading, isClient, currentPath, isAuthenticated, isPassivePublic]);

  // Route protection logic
  useEffect(() => {
    // ✅ FIXED: Better condition for initialization check
    const isStillInitializing = !initialCheckDone && (loading || (hasInitialized.current && !isPublicRoute(router.pathname)));
    
    if (!isClient || isStillInitializing) {
      setRouteChecked(false);
      return;
    }

    const currentPath = router.pathname;
    const routeConfig = getRouteConfig(currentPath);

    console.log('🛡️ AppGuard route protection:', {
      path: currentPath,
      isAuthenticated,
      userRole: user?.role,
      routeConfig,
      initialCheckDone
    });

    // Handle public routes
    if (isPublicRoute(currentPath)) {
      console.log('📖 Public route detected');
      
      if (isAuthenticated && routeConfig?.redirectIfAuthenticated && user?.role) {
        const redirectPath = getRoleBasedRedirect(user.role);
        if (redirectPath !== currentPath) {
          console.log('🔄 Authenticated user on public route, redirecting to:', redirectPath);
          router.replace(redirectPath);
          return;
        }
      }
      
      console.log('✅ Public route access granted');
      setRouteChecked(true);
      return;
    }

    // Handle protected routes
    if (!isAuthenticated || !user) {
      console.log('🚫 Not authenticated, redirecting to login');
      router.replace('/auth/login');
      return;
    }
  if (!isAuthenticated && user) {
      console.log('🚫 Not authenticated, redirecting to login');
      router.replace('/auth/login');
      return;
    }
    // Role-based protection
    if (routeConfig?.allowedRoles && routeConfig.allowedRoles.length > 0) {
      const hasPermission = routeConfig.allowedRoles.some(role => 
        user.role?.toLowerCase() === role.toLowerCase()
      );
      
      if (!hasPermission) {
        const userDashboard = getRoleBasedRedirect(user.role);
        console.log('🚫 No permission, redirecting to:', userDashboard);
        router.replace(userDashboard);
        return;
      }
    }

    // Handle unknown routes for authenticated users
    if (!routeConfig && isAuthenticated && user) {
      const userDashboard = getRoleBasedRedirect(user.role);
      console.log('❓ Unknown route, redirecting to:', userDashboard);
      router.replace(userDashboard);
      return;
    }




   if (initialCheckDone && !isAuthenticated && !user && !loading) {
      const noCookies = !document.cookie.includes('access_token') && 
                       !document.cookie.includes('session') &&
                       !document.cookie.includes('auth');
      
      const isOnProtectedRoute = !isPublicRoute(currentPath);
      
      // Additional conditions for logout detection
      const userLoggedOut = noCookies && isOnProtectedRoute;
      
      if (userLoggedOut) {
        console.log('🔓 User logged out detected - redirecting to login');
        console.log('🔓 Logout conditions:', {
          initialCheckDone,
          isAuthenticated,
          hasUser: !!user,
          loading,
          noCookies,
          isOnProtectedRoute,
          currentPath
        });
        
       
        router.replace('/auth/login');
        return;
      }
    }

    console.log('✅ Route protection passed');
    setRouteChecked(true);

  }, [isClient, initialCheckDone, isAuthenticated, user, router.pathname, loading, router]);

  // Passive public marketing/legal pages render directly — no auth-bound
  // loading screens, no SSR/hydration flash, no CHECK_AUTH side effects.
  if (isPassivePublic) {
    return children;
  }

  // Loading states
  if (!isClient) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Progress value={30} className="w-32 mb-4" />
          <h2 className="text-lg font-semibold text-gray-700">Initializing...</h2>
        </div>
      </div>
    );
  }

  // ✅ FIXED: Better loading condition
  const isStillLoading = !initialCheckDone && (loading || (hasInitialized.current && !isPublicRoute(router.pathname)));

  if (isStillLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Progress value={60} className="w-32 mb-4" />
          <h2 className="text-lg font-semibold text-gray-700">
            Checking Authentication...
          </h2>
          <p className="text-sm text-gray-500 mt-2">Please wait</p>
        </div>
      </div>
    );
  }

  if (!routeChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Progress value={90} className="w-32 mb-4" />
          <h2 className="text-lg font-semibold text-gray-700">Loading...</h2>
        </div>
      </div>
    );
  }

  console.log('✅ AppGuard rendering children');
  return children;
};

export default AppGuard;
